import * as React from "react";

import { getDocument } from "../../utils/document";

import { trackToSegment, identifyToSegment, pageToSegment } from "../segment";

export function useTrackEvent(args: {
  event: string;
  properties?: {
    name?: any;
    href?: string;
    copy?: string;
  };
  now?: boolean;
}) {
  return useTrack({ ...args, eventType: "event" });
}

export function useTrackIdentity(args) {
  return useTrack({ ...args, eventType: "identify" });
}

export function useTrackPage(args) {
  return useTrack({ ...args, eventType: "page" });
}

/*
 * Send tracking events to our user analytics provider.
 *
 * useTrackEvent can operate in two modes: immediate mode (now), or
 * lazy mode. By passing in the option "now: true" the event will be
 * sent as a side-effect, running only once. This allows for tracking
 * of events on the first mount of a component:
 *
 *   function MyComponent() {
 *     useTrackEvent({ event: 'MyComponent Loaded', now: true })
 *   }
 *
 * In lazy mode, the returned function "trackEvent" can be called to
 * send the event at any time:
 *
 *   function MyComponent() {
 *     const trackClick = useTrackEvent({ event: 'Button Clicked' })
 *     return (
 *       <button onClick={trackClick}>Track me</button>
 *     )
 *   }
 */
function useTrack(useArgs: TODO = {}) {
  const {
    now,
    location,
    properties: useProperties = {},
    eventType,
    ...otherUseArgs
  } = useArgs;
  function trackEvent(sendArgs: TODO = {}) {
    const { properties: sendProperties = {}, ...otherSendArgs } = sendArgs;
    const finalArgs = {
      properties: {
        ...(location ? { location } : {}),
        ...useProperties,
        ...sendProperties,
      },
      ...otherUseArgs,
      ...otherSendArgs,
    };
    if (eventType === "identify") {
      identifyToSegment(finalArgs);
    } else if (eventType === "page") {
      if (!finalArgs?.name) {
        finalArgs.name = getDocument()?.title;
      }
      pageToSegment(finalArgs);
    } else {
      trackToSegment(finalArgs);
    }
  }
  React.useEffect(() => {
    if (now) {
      trackEvent();
    }
  }, []);
  return trackEvent;
}
